import { Input, InputProps } from '@euler/components/form/Input';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  NativeSyntheticEvent,
  TextInputEndEditingEventData,
  TextInputFocusEventData,
} from 'react-native';

export type InputNumberFeedback = 'show-invalid-state' | 'reset';

export type InputNumberProps = Omit<InputProps, 'value'> & {
  value?: number;
  onRequestFeedback?: (type: InputNumberFeedback) => void;
  onChangeValue?: (value: number | undefined) => void;
};

const validate = (s: string) =>
  /^\s*[+-]?\d+(\.(\d+)?)?\s*$/.test(s) || /^\s*$/.test(s);

const isPrefix = (s: string) => /^\s*[+-]$/.test(s);

const stringRepresentation = (value: number | undefined) =>
  value != null ? String(value) : '';

export const InputNumber = memo(
  ({
    onFocus,
    onBlur,
    onChangeText,
    onEndEditing,
    value,
    onChangeValue,
    onRequestFeedback,
    ...props
  }: InputNumberProps) => {
    const [text, setText] = useState(stringRepresentation(value));
    const lastValue = useRef(text);
    const lastFeedback = useRef<InputNumberFeedback>();
    const isValid = useRef(validate(text));

    useEffect(() => {
      const str = stringRepresentation(value);
      if (lastValue.current !== str) {
        lastValue.current = str;
        setText(str);
      }
    }, [value]);

    const onInternalChangeText = useCallback(
      (s: string) => {
        if (!isPrefix(s)) {
          s = s.replace(/[^+\-0-9.]/g, '').replace(/\.{2,}/g, '.');
        }
        lastValue.current = s;
        setText(s);
        onChangeText?.(s);

        isValid.current = false;

        let feedback: InputNumberFeedback = 'show-invalid-state';
        if (validate(s)) {
          const v = /^\s*$/.test(s) ? undefined : Number(s);
          onChangeValue?.(v);
          feedback = 'reset';
          isValid.current = true;
        } else if (isPrefix(s)) {
          feedback = 'reset';
        }

        if (feedback !== lastFeedback.current) {
          lastFeedback.current = feedback;
          onRequestFeedback?.(feedback);
        }
      },
      [onChangeText, onChangeValue, onRequestFeedback],
    );

    const onInternalEndEditing = useCallback(
      (e: NativeSyntheticEvent<TextInputEndEditingEventData>) => {
        onEndEditing?.(e);
      },
      [onEndEditing],
    );

    const onInternalBlur = useCallback(
      (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
        if (!isValid.current) {
          onRequestFeedback?.('show-invalid-state');
        }
        onBlur?.(e);
      },
      [onBlur, onRequestFeedback],
    );

    const onInternalFocus = useCallback(
      (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
        onRequestFeedback?.('reset');
        onFocus?.(e);
      },
      [onFocus, onRequestFeedback],
    );
    return (
      <Input
        numberOfLines={1}
        {...props}
        value={text}
        onChangeText={onInternalChangeText}
        onFocus={onInternalFocus}
        onBlur={onInternalBlur}
        onEndEditing={onInternalEndEditing}
      />
    );
  },
);
